export const pains: string[] = [
  'A parent is three weeks behind on tuition and you still have not sent the message.',
  'Your policies live in your head, so every exception becomes a negotiation.',
  'A licensing visit is due and you do not know which folder the immunization records are in.',
  'You have not raised your rate in three years because you do not know how to announce it.',
  'You are the substitute, the cook, the bookkeeper and the director — on the same Tuesday.',
  'A child gets sick and you are guessing whether they can come back tomorrow.',
  'The one assistant you trained left, and everything she knew left with her.',
  'You work eleven hours a day and cannot say what your program actually earned last month.',
];

export const painBridge =
  'None of this is a caring problem. It is a systems problem — and systems can be handed to you.';

export const bigIdea = {
  heading: 'You were trained to care for children. Nobody handed you the manual for the business.',
  payoff: 'A program without a written system is not a business. It is an improvisation with children in it.',
  body:
    'Every licensed program in the country runs on the same handful of documents: an agreement that says what you are owed, protocols that say what happens when a child is sick, a schedule that survives your absence, and records that satisfy a licensor. The programs that feel calm are not staffed by calmer people. They are the ones where all of that was written down once, on purpose, and then simply followed.',
};

export const productPresentation = {
  badge: 'THE SYSTEM',
  heading: 'The Daycare Operating System™',
  tagline: 'Six volumes. One working program.',
  body:
    'This is the operating manual for a home daycare or small child care center — the documents, protocols, checklists and scripts that run the program instead of you running it from memory. It is not a course, not a community, and not a motivational read. It is the binder you were supposed to be given on day one.',
};

export const transformationRows: { before: string; after: string }[] = [
  {
    before: 'Policies you explain differently to every family.',
    after: 'One signed agreement that says the same thing to everyone.',
  },
  {
    before: 'Chasing late tuition when you finally get the nerve.',
    after: 'A written payment ladder that runs on a date, not on your nerve.',
  },
  {
    before: 'A licensing visit that costs you a week of sleep.',
    after: 'A labeled binder handed over in four minutes.',
  },
  {
    before: 'Your rate frozen for three years.',
    after: 'An annual increase announced with a script that holds.',
  },
  {
    before: 'Everything stops when you are sick.',
    after: 'A substitute binder that lets the day run without you.',
  },
  {
    before: 'No idea what the program actually earns.',
    after: 'A rate worksheet and a deduction list you review every quarter.',
  },
];

export const transformationHeading = 'What changes in the first thirty days';

export const costRows: { problem: string; cost: string }[] = [
  { problem: 'One family, three weeks of unpaid tuition', cost: '$675 you will not get back' },
  { problem: 'One empty spot left unfilled for a month', cost: '$900 in gone revenue' },
  { problem: 'A licensing citation and its corrective plan', cost: 'Fees, a re-visit, and your reputation' },
  { problem: 'A rate you have not raised in three years', cost: '$4,200 a year of your own money' },
  { problem: 'Six administrative hours every week', cost: '312 hours a year, unpaid' },
  { problem: 'One undocumented incident', cost: 'A dispute you cannot win on memory' },
];

export const costHeading = 'What the missing system is already costing you';
export const costPayoff = 'Every one of those lines is cheaper to fix than to keep.';
export const costSubPayoff = 'THE ENTIRE SYSTEM COSTS LESS THAN ONE UNPAID DAY.';

export const closeCompare: { without: string; with: string }[] = [
  { without: 'Another year of improvising', with: 'A written system by the weekend' },
  { without: 'Money quietly leaking out', with: 'Every dollar accounted for on paper' },
  { without: 'The program depends on you being there', with: 'The program depends on the binder' },
];

export const closeContent = {
  heading: 'You already do the hardest part of this job. Let the paperwork stop being the hard part.',
  body:
    'You are not missing dedication, patience or skill — you have been carrying a business on undocumented judgment for years. Six volumes, one afternoon of setup, and the program stops living in your head.',
  kicker: 'SIX VOLUMES · FIVE BONUSES · SEVEN-DAY GUARANTEE',
  offer: 'Everything above, normally $97 — today $27 USD.',
};

export const authorityStats = [
  { value: '41%', label: 'of child care programs run under capacity' },
  { value: '53%', label: 'of child care leaders name staffing their #1 concern' },
  { value: '43%', label: 'name burnout as a major challenge' },
  { value: '$14.60/hr', label: 'median child care worker wage' },
];

export const authoritySource =
  'Sources: Procare 2026 Child Care Business Trends Report (~5,000 respondents), Bureau of Labor Statistics.';

export const heroContent = {
  badge: 'FOR HOME DAYCARE PROVIDERS & SMALL CENTER DIRECTORS',
  heading: 'The operating manual your daycare was supposed to come with.',
  subheading:
    'Six volumes covering licensing, contracts, tuition collection, ratios, safety protocols and taxes — the complete written system that runs a small child care program. Not a course. A manual you print, sign and use this week.',
  miniBadges: [
    '📕 6 volumes',
    '📄 200+ pages',
    '🖨 28 printable forms',
    '🗣 60+ word-for-word scripts',
    '⬇ Instant download',
    '🛡 7-day guarantee',
  ],
  cta: 'Get the system',
  imageAlt:
    'The six volumes of The Daycare Operating System standing upright — navy hardcovers with gold-foil titles and numbered brass volume bands, beside a ruled checklist pad',
};

export const guarantee = {
  seal: '7 DAYS',
  heading: 'Read all six volumes. Then decide.',
  highlight: 'If it does not change how your program runs, you do not pay for it.',
  body:
    'Download everything, print what you need, put the Fill Engine to work this week. If within seven days it has not earned its place on your shelf, write to [SUPPORT_EMAIL] and you get every cent back — and you keep the files. No form, no explanation, no follow-up questions.',
};

export const urgency = {
  kicker: 'LAUNCH PRICING ENDS IN',
  postCountdown: 'Launch pricing has ended. The system is now $97.',
  progressLabel: '88% of launch-price copies claimed',
  progress: 88,
};

export const pricing = {
  heading: 'Everything you get for $27',
  subheading: 'One payment. Instant download. Yours to print forever.',
  volumesLabel: 'The six volumes',
  bonusesLabel: 'The five bonuses',
  volumeList: [
    'Vol. 01 — Open Full: The Licensed Launch Path',
    'Vol. 02 — The Fill Engine: Never an Empty Seat',
    'Vol. 03 — The Keep Engine: Staff & Families That Stay',
    'Vol. 04 — Paper Armor: The Inspection-Ready Binder',
    'Vol. 05 — Money That Stays',
    'Vol. 06 — Run Without Drowning',
  ],
  bonusList: [
    'Daycare Clarity Sprint: 7-Day Startup Action Plan — $47',
    'Parent Tour Conversion Script Pack — $67',
    'Inspection Binder Fast-Start Checklist — $49',
    'Daycare Pricing Confidence Worksheet — $59',
    'First 10 Families Outreach Kit — $42',
  ],
  badges: ['SECURE CHECKOUT', 'INSTANT ACCESS', '7-DAY GUARANTEE'],
  disclaimer: 'You will be charged in your local currency.',
  cta: 'Get instant access — $27',
};

export const footer = {
  brand: 'The Daycare Operating System™',
  identity: 'An operating manual for home daycare providers and small center directors.',
  links: ['Terms', 'Privacy', 'Refund policy', 'Contact'],
  legalOne:
    'This publication is an operating manual, not legal, tax or licensing advice. Verify all requirements with your state licensing authority.',
  legalTwo: `© ${new Date().getFullYear()} The Daycare Operating System™. All rights reserved. Support: [SUPPORT_EMAIL]`,
};
